import {
  Space,
  Typography,
  Input,
  Select,
  Pagination,
  Table,
  DatePicker,
  Button,
} from 'antd';
import type { TableProps as AntdTableProps } from 'antd';
import { useEffect, useRef, useState } from 'react';

import NoData from 'src/assets/images/no-data.png';
import './index.scss';
import { SvgIcon } from '@/components/icon';
import { useSearchParams } from 'react-router-dom';
import { BrandNameStatus, ReferenceStatus, ReviewsStatus } from '@/types';
import { CloudDownloadOutlined } from '@ant-design/icons';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface TableProps {
  data: any[];
  columns: any[];
  filteredData: any[];
  setFilteredData: (data: any[]) => void;
  label: string;
  isDetailed?: boolean;
  // Optional caption rendered under the `isDetailed` heading, explaining what
  // the list contains. Undefined by default, so every other admin table keeps
  // a bare heading exactly as before. Only rendered in the `isDetailed`
  // layout — the compact one puts the heading inside the actions row, where
  // there is no line for a caption.
  description?: React.ReactNode;
  isDatePicker?: boolean;
  isBrandNameStatus?: boolean;
  isReferenceStatus?: boolean;
  expandable?: {
    expandedRowRender: (record: any) => JSX.Element;
    rowExpandable: (record: any) => boolean;
  };
  isDownload?: boolean;
  downloadData?: (record: any) => void;
  downloadLoading?: boolean;
  total: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  limit: number;
  setLimit: React.Dispatch<React.SetStateAction<number>>;
  isReviewStatus?: boolean;
  isDisabled?: boolean;
  scroll?: { x?: string | number | true; y?: string | number };
  paginationPosition?: 'top' | 'bottom';
  customFilters?: React.ReactNode;
  // Forwarded to the antd Table below (TCX-903: Free Listing Management's
  // clickable rows). Typed explicitly so callers get real type-checking
  // instead of relying on the `...props` spread's untyped passthrough.
  onRow?: AntdTableProps<any>['onRow'];
  // F4/TCX-903: bump this (e.g. an incrementing counter) whenever the caller
  // programmatically clears search-adjacent state — a "Clear Filters" action
  // that empties the URL out from under this component's own debounced
  // search-to-URL write. Optional and inert unless a caller opts in, so
  // every other consumer of this shared table is unaffected.
  clearSearchSignal?: number;
  // Mobile layout (TCX-903 Free Listing Management): when provided, renders
  // this instead of the antd <Table> for non-empty data — e.g. a caller's
  // own one-card-per-row list. The "No Data" empty state below is untouched
  // either way, so every caller keeps the exact same empty-state look.
  // Optional and inert unless a caller opts in (undefined by default), so
  // every other consumer of this shared table renders the antd <Table>
  // exactly as before. This is the mechanism that lets a page swap its row
  // presentation for mobile WITHOUT forking the search-box debounce/URL
  // sync or the pagination controls below, both of which stay owned by this
  // single component regardless of which body is rendered.
  renderList?: (data: any[]) => React.ReactNode;
  // Mobile layout: when true, renders the pagination controls as a single
  // compact, always-one-line control (antd's `simple` Pagination, and the
  // per-page-size <Select> hidden) instead of the default selector +
  // numbered-pages row, which wraps onto two lines under ~400px. Optional,
  // defaults to false, so every other consumer's pagination is unaffected.
  compactPagination?: boolean;
}

interface DateRange {
  startDate: string;
  endDate: string;
}

export default function CustomTable({
  columns,
  filteredData,
  label,
  isDetailed,
  description,
  isDatePicker,
  isBrandNameStatus,
  isReferenceStatus = false,
  expandable,
  total,
  page,
  setPage,
  limit,
  setLimit,
  isDownload = true,
  downloadData,
  downloadLoading = false,
  isReviewStatus,
  isDisabled = false,
  scroll,
  paginationPosition = 'top',
  customFilters,
  clearSearchSignal,
  renderList,
  compactPagination = false,
  ...props
}: TableProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState<string>(
    () => searchParams.get('search') ?? '',
  );
  // F4: skips the very first run of the reset effect below, so a caller that
  // starts `clearSearchSignal` at e.g. `0` doesn't wipe out a value this
  // component just seeded from the URL on mount (a bookmarked `?search=...`
  // link). Only a CHANGE to the signal after mount counts as "clear now".
  const isFirstClearSignalRun = useRef(true);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [status, setStatus] = useState<BrandNameStatus | ReferenceStatus>(
    isReferenceStatus ? ReferenceStatus.PENDING : BrandNameStatus.PENDING,
  );
  const [statusReview, setStatusReview] = useState<ReviewsStatus>();

  // Search Handler
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    setPage(1);
  };

  const removeEmptyParams = (params: URLSearchParams) => {
    const newParams = new URLSearchParams();
    for (const [key, value] of params.entries()) {
      if (value !== '') {
        newParams.set(key, value);
      }
    }
    return newParams;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (searchTerm.length > 0) {
          newParams.set('search', searchTerm);
        } else {
          newParams.delete('search');
        }
        return newParams;
      });
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, setSearchParams]);

  // F4: `searchTerm` is local state, seeded from the URL only once on mount —
  // a caller like the Free Listing page's "Clear Filters" (which empties
  // every URL param directly) has no other way to reach it. Watching the URL
  // for that instead does not work in general: if the user cleared before the
  // 300ms debounce above ever wrote `search` to the URL, the key was already
  // absent both before and after the clear, so nothing about the URL
  // actually changes for an effect to react to. `clearSearchSignal` is an
  // explicit, opt-in escape hatch for exactly this — bump it (e.g.
  // increment a counter) whenever such a caller clears filters. Changing
  // `searchTerm` here also cancels (via the effect above's cleanup) whatever
  // debounced write was still pending, so a stale keystroke can't land after
  // the clear.
  useEffect(() => {
    if (clearSearchSignal === undefined) return;
    if (isFirstClearSignalRun.current) {
      isFirstClearSignalRun.current = false;
      return;
    }
    setSearchTerm('');
  }, [clearSearchSignal]);

  useEffect(() => {
    if (dateRange) {
      setSearchParams((params) => {
        const newParams = new URLSearchParams(params);
        newParams.set('startDate', dateRange.startDate);
        newParams.set('endDate', dateRange.endDate);
        return removeEmptyParams(newParams);
      });
      setPage(1);
    }
  }, [dateRange]);

  useEffect(() => {
    if (status && (isBrandNameStatus || isReferenceStatus)) {
      setSearchParams((params) => {
        const newParams = new URLSearchParams(params);
        newParams.set('status', status);
        return removeEmptyParams(newParams);
      });
      setPage(1);
    }
  }, [status]);

  useEffect(() => {
    if (statusReview && isReviewStatus) {
      setSearchParams((params) => {
        const newParams = new URLSearchParams(params);
        newParams.set('status', statusReview);
        return removeEmptyParams(newParams);
      });
      setPage(1);
    } else if (isReviewStatus && !statusReview) {
      setSearchParams((params) => {
        const newParams = new URLSearchParams(params);
        newParams.set('status', '');
        return removeEmptyParams(newParams);
      });
      setPage(1);
    }
  }, [statusReview]);

  const getCustomLabel = (size: number) => {
    const start =
      (page - 1) * size + (Math.min(page * size, total) == 0 ? 0 : 1);
    const end = Math.min(page * size, total);
    return `${start}-${end} of ${total}`;
  };

  const renderPaginationControls = () => (
    <div className="tables-actions-pagination">
      {/* Mobile (compactPagination): the per-page-size selector is dropped
          rather than squeezed — it and a numbered Pagination together are
          what wraps onto a second line under ~400px (the measured TCX-903
          defect). The page size itself is still fully controllable from a
          wider viewport; this only trims the compact one-line control. */}
      {!compactPagination && (
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div className="table-selector">
            <Select
              className="table-selector-select"
              style={{ width: 120 }}
              value={limit}
              size="small"
              onChange={(size) => {
                setLimit(size);
                setPage(1);
              }}
              optionLabelProp="label"
            >
              <Option value={5} label={getCustomLabel(5)}>
                5 per page
              </Option>
              <Option value={10} label={getCustomLabel(10)}>
                10 per page
              </Option>
              <Option value={20} label={getCustomLabel(20)}>
                20 per page
              </Option>
            </Select>
          </div>
        </div>
      )}
      <Pagination
        current={page}
        pageSize={limit}
        total={total}
        onChange={(page) => setPage(page)}
        showSizeChanger={false}
        // `simple` collapses to a "prev / current of total (editable) / next"
        // control that stays on one line at any width, still lets an admin
        // jump straight to a page (typing into the current-page field), so
        // paging remains fully functional — it is just visually compact.
        simple={compactPagination}
        size={compactPagination ? 'small' : undefined}
        className="tables-actions-pagination"
        style={{
          marginBottom: '0',
          marginLeft: compactPagination ? 'auto' : undefined,
        }}
      />
    </div>
  );

  return (
    <div className="tables-wrapper">
      {isDetailed && (
        <>
          <Typography.Title
            level={3}
            style={{
              textWrap: 'nowrap',
              margin: 16,
              // The card (`.tables-wrapper`) already opens with 24px of its
              // own top padding; the heading's own 16px on top of that pushed
              // the title 40px down from the card edge. Drop it so the block
              // starts where the card does.
              marginTop: description ? 0 : undefined,
              // With a caption below, heading and caption should read as one
              // block rather than two separately spaced lines. Without one,
              // the margin stays exactly as it was.
              marginBottom: description ? 4 : undefined,
            }}
          >
            {label}
          </Typography.Title>
          {description && (
            <Typography.Paragraph
              type="secondary"
              // Bottom margin is deliberately larger than the 16px the
              // actions row below carries: adjacent block margins collapse to
              // the larger of the two, so this is what actually sets the gap
              // separating the heading block from the search/filters row.
              style={{ margin: '0 16px 32px' }}
            >
              {description}
            </Typography.Paragraph>
          )}
        </>
      )}
      {/* Actions Section (Search and Pagination Controls) */}
      <div className="tables-actions" style={{ margin: '16px' }}>
        <Space
          direction="horizontal"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div className="tables-actions-search">
            {/* Naming */}
            {!isDetailed && (
              <Typography.Title
                level={3}
                style={{ textWrap: 'nowrap', marginBlock: 16 }}
              >
                {label}
              </Typography.Title>
            )}
            {/* Search Bar */}
            <Input.Search
              placeholder="Search"
              enterButton={
                <SvgIcon
                  type={'search'}
                  height={12}
                  width={12}
                  style={{ stroke: 'white' }}
                />
              }
              style={{ maxWidth: '300px' }}
              value={searchTerm}
              onChange={handleSearch}
            />
            {/* Custom Filters or Date Picker */}
            {customFilters}
            {isDatePicker && !customFilters && (
              <RangePicker
                onChange={(_, v) => {
                  setDateRange({
                    startDate: v[0],
                    endDate: v[1],
                  });
                }}
              />
            )}
            {/* Brand Name Status */}
            {isBrandNameStatus && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography.Text
                  style={{ textWrap: 'nowrap', color: '#757A89' }}
                >
                  Status:
                </Typography.Text>

                <Select
                  className="table-selector-select"
                  style={{ width: 170, borderRadius: 4 }}
                  value={status}
                  size="small"
                  onChange={(size) => {
                    setStatus(size);
                    setPage(1);
                  }}
                  optionLabelProp="label"
                >
                  <Option value={BrandNameStatus.APPROVED} label="Approved">
                    Approved
                  </Option>
                  <Option value={BrandNameStatus.PENDING} label="Pending">
                    Pending
                  </Option>
                  <Option value={BrandNameStatus.REJECTED} label="Rejected">
                    Rejected
                  </Option>
                </Select>
              </div>
            )}
            {/* References status */}
            {isReferenceStatus && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography.Text
                  style={{ textWrap: 'nowrap', color: '#757A89' }}
                >
                  Status:
                </Typography.Text>

                <Select
                  className="table-selector-select"
                  style={{ width: 170, borderRadius: 4 }}
                  value={status}
                  size="small"
                  onChange={(size) => {
                    setStatus(size);
                    setPage(1);
                  }}
                  optionLabelProp="label"
                >
                  <Option value={ReferenceStatus.APPROVED} label="Approved">
                    Approved
                  </Option>
                  <Option value={ReferenceStatus.PENDING} label="Pending">
                    Pending
                  </Option>
                  <Option value={ReferenceStatus.REJECTED} label="Rejected">
                    Rejected
                  </Option>
                </Select>
              </div>
            )}
            {isReviewStatus && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography.Text
                  style={{ textWrap: 'nowrap', color: '#757A89' }}
                >
                  Status:
                </Typography.Text>

                <Select
                  className="table-selector-select"
                  style={{ width: 170, borderRadius: 4 }}
                  value={statusReview}
                  placeholder="Select Status"
                  size="small"
                  onChange={(size) => {
                    setStatusReview(size);
                    setPage(1);
                  }}
                  optionLabelProp="label"
                  allowClear
                >
                  <Option value={ReviewsStatus.FUTURE} label="Future">
                    Future
                  </Option>
                  <Option value={ReviewsStatus.PENDING} label="Pending">
                    Pending
                  </Option>
                  <Option value={ReviewsStatus.COMPLETED} label="completed">
                    Completed
                  </Option>
                  <Option value={ReviewsStatus.DECLINED} label="Declined">
                    Declined
                  </Option>
                </Select>
              </div>
            )}
            {/* Download table button */}
            {isDownload && (
              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Button
                  type="default"
                  icon={<CloudDownloadOutlined style={{ fontSize: 16 }} />}
                  className="admin-button-download-table"
                  onClick={downloadData}
                  loading={downloadLoading}
                  disabled={
                    (filteredData && filteredData?.length === 0) ||
                    isDisabled ||
                    downloadLoading
                  }
                >
                  Download Table
                </Button>
              </div>
            )}
          </div>
          {paginationPosition === 'top' && renderPaginationControls()}
        </Space>
      </div>

      {filteredData.length === 0 ? (
        <div className="no-data-wrapper">
          <img src={NoData} alt="No Data" />
          <Typography.Text
            style={{
              color: '#9FA1AD',
              fontWeight: 500,
              maxWidth: '200px',
              textAlign: 'center',
            }}
          >
            {`It looks like there aren't any ${label.toLowerCase()}.`}
          </Typography.Text>
        </div>
      ) : renderList ? (
        // Mobile layout: caller-supplied body (e.g. one card per row)
        // instead of the antd <Table>. Search, filters and pagination above
        // are unaffected — they belong to this component either way.
        renderList(filteredData)
      ) : (
        <Table
          dataSource={filteredData}
          columns={columns}
          pagination={false}
          style={{ marginTop: '0' }}
          scroll={scroll}
          expandable={expandable}
          {...props}
        />
      )}

      {paginationPosition === 'bottom' && renderPaginationControls()}
    </div>
  );
}
